import { useEditor, EditorContent, generateHTML, BubbleMenu, Extension } from '@tiptap/react';
import { TextSplitterExtension, IOnSplitParams, ContextMenu } from '@ws/shared/components';
import { AlignCenterV16, List16 } from '@ws/shared/icons';
import { getResolution, isMobile, TK_TEXT_SERIALIZERS } from '@ws/shared/utils';
import {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { TStatsCounter } from '../../../redux/stats';
import { scrollCaretIntoView } from '../../../utils/scrollCaretIntoView/scrollCaretIntoView';
import { getLengthFromText } from '../../../utils/stats/getTextLengthFromHtml';
import { INoteSplitterChildrenParams, TOnChange } from '../types';

import { extensions } from './extensions';

interface IMenuPosition {
  x: number;
  y: number;
  children: ReactNode;
}

// Передаем ref через forwardRef
const MenuBox = forwardRef<HTMLDivElement, IMenuPosition>((props, ref) => {
  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        top: `${props.y}px`,
        left: `${props.x}px`,
      }}
    >
      {props.children}
    </div>
  );
});

MenuBox.displayName = 'MenuBox';

interface TipTapEditor {
  noteId: string;
  note: string;
  onBlur: () => void;
  onChange: TOnChange;
  onCounterUpdate?: (counter: TStatsCounter) => void;
  onCutUpperPart: INoteSplitterChildrenParams['onSplitNoteAbove'];
  onCutLowerPart: INoteSplitterChildrenParams['onSplitNoteBelow'];
}

type TMenuPosition = {
  clientX: number;
  clientY: number;
  layerX: number;
  layerY: number;
  offsetX: number;
  offsetY: number;
  pageX: number;
  pageY: number;
  x: number;
  y: number;
};
const defaultMenuPosition: TMenuPosition = {
  clientX: 0,
  clientY: 0,
  layerX: 0,
  layerY: 0,
  offsetX: 0,
  offsetY: 0,
  pageX: 0,
  pageY: 0,
  x: 0,
  y: 0,
};

const CustomEnterBehavior = Extension.create({
  name: 'customEnterBehavior',

  addKeyboardShortcuts() {
    return {
      Enter: () => {
        // Обычный Enter - просто перенос строки (вставляем <br>)
        return this.editor.commands.setHardBreak();
      },
      'Shift-Enter': () => {
        // Shift+Enter - создание нового параграфа
        return this.editor.commands.splitBlock();
      },
    };
  },
});

export const TipTapEditor = forwardRef<HTMLElement, TipTapEditor>(
  ({ noteId, note, onBlur, onChange, onCounterUpdate, onCutUpperPart, onCutLowerPart }, ref) => {
    const resolution = getResolution();
    const mobile = resolution && isMobile(resolution);

    const [menuVisible, setMenuVisible] = useState(false);
    const [menuPosition, setMenuPosition] = useState<TMenuPosition>(defaultMenuPosition);

    const menuRef = useRef<HTMLDivElement | null>(null);
    const editorRef = useRef<HTMLElement | null>(null);
    const noteIdRef = useRef<string>(noteId);

    // TipTap фиксирует options-колбэки в момент создания редактора (useEditor) —
    // всё, что нужно им актуальным, читается через этот ref, иначе замыкания
    // навсегда видят значения первого рендера и правки не сохраняются (F19).
    // useLayoutEffect, не useEffect: между commit и пассивным эффектом редактор
    // уже может дёрнуть колбэк.
    const latestProps = useRef({
      onChange,
      onBlur,
      onCounterUpdate,
      onCutUpperPart,
      onCutLowerPart,
      mobile,
    });
    useLayoutEffect(() => {
      latestProps.current = {
        onChange,
        onBlur,
        onCounterUpdate,
        onCutUpperPart,
        onCutLowerPart,
        mobile,
      };
    });

    // ======= SPLIT FUNCTIONS
    // Стабильные (deps: []): уходят в TextSplitterExtension.configure внутри useEditor
    // и тоже замораживаются — актуальность берут из latestProps.
    const handleUpperPartSplit = useCallback((params: IOnSplitParams) => {
      const newContentString = generateHTML(params.separated, extensions);
      const oldContentString = generateHTML(params.updatedContent, extensions);

      latestProps.current.onCutUpperPart(newContentString, () => {
        latestProps.current.onChange(oldContentString, { saveImmediately: true });
      });
    }, []);

    const handleLowerPartSplit = useCallback((params: IOnSplitParams) => {
      const newContentString = generateHTML(params.separated, extensions);
      const oldContentString = generateHTML(params.updatedContent, extensions);

      latestProps.current.onCutLowerPart(newContentString, () => {
        latestProps.current.onChange(oldContentString, { saveImmediately: true });
      });
    }, []);
    // ======= SPLIT FUNCTIONS ^

    // Обработчик правого клика
    const handleContextMenu = (event: any) => {
      if (event.shiftKey) {
        return;
      }

      event.preventDefault(); // Отключаем стандартное контекстное меню браузера

      setMenuPosition({
        clientX: event.clientX,
        clientY: event.clientY,
        layerX: event.layerX,
        layerY: event.layerY,
        offsetX: event.offsetX,
        offsetY: event.offsetY,
        pageX: event.pageX,
        pageY: event.pageY,
        x: event.x,
        y: event.y,
      });
      setMenuVisible(true);
    };

    const editor = useEditor({
      extensions: [
        ...extensions,
        CustomEnterBehavior,
        TextSplitterExtension.configure({
          onUpperPartSplit: handleUpperPartSplit,
          onLowerPartSplit: handleLowerPartSplit,
        }),
      ],
      content: note,
      editable: true,
      onBlur: () => {
        latestProps.current.onBlur();
      },
      onUpdate: ({ editor: innerEditor }) => {
        latestProps.current.onChange(innerEditor.getHTML());
        latestProps.current.onCounterUpdate?.(
          getLengthFromText(innerEditor.getText({ textSerializers: TK_TEXT_SERIALIZERS })),
        );
      },
      onSelectionUpdate: ({ editor: innerEditor }) => {
        if (latestProps.current.mobile && innerEditor.isFocused) {
          scrollCaretIntoView(innerEditor.view);
        }
      },
      onCreate: ({ editor: innerEditor }) => {
        // Привязываем обработчик события contextmenu к DOM-элементу редактора
        const editorElement = innerEditor.view.dom;
        editorElement.addEventListener('contextmenu', handleContextMenu);

        editorRef.current = innerEditor.view.dom;
      },
      onDestroy: () => {
        if (!editor) {
          return;
        }

        // Убираем обработчик при уничтожении редактора
        const editorElement = editor.view.dom;
        editorElement.removeEventListener('contextmenu', handleContextMenu);
      },
    });

    useImperativeHandle(
      ref,
      () => {
        // eslint-disable-next-line
        return editor?.view.dom!;
      },
      [editor],
    );

    // На iOS клавиатура выезжает анимированно уже ПОСЛЕ того, как сработал onSelectionUpdate
    // (в момент тапа visualViewport ещё не сжат, поэтому scrollCaretIntoView может решить,
    // что скролл не нужен). Когда клавиатура доезжает и --vv-height схлопывает высоту
    // контейнера, ничего не пересчитывает позицию каретки заново — досылаем это здесь.
    useEffect(() => {
      if (!editor || !mobile) {
        return;
      }

      const viewport = window.visualViewport;

      if (!viewport) {
        return;
      }

      const handleViewportResize = () => {
        if (editor.isFocused) {
          scrollCaretIntoView(editor.view);
        }
      };

      viewport.addEventListener('resize', handleViewportResize);

      return () => {
        viewport.removeEventListener('resize', handleViewportResize);
      };
    }, [editor, mobile]);

    // Закрытие меню при клике вне его
    useEffect(() => {
      const handleClickOutside = (event: any) => {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
          setMenuVisible(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    useEffect(() => {
      if (noteIdRef.current !== noteId) {
        noteIdRef.current = noteId;

        if (!editor) {
          return;
        }

        editor.commands.setContent(note);
      } else if (editor) {
        // Content updated from outside (e.g. sync merge)
        const currentContent = editor.getHTML();
        // Check equality to avoid loop on simple echoes
        if (currentContent !== note) {
          // Attempt to preserve selection
          const { from, to } = editor.state.selection;
          editor.commands.setContent(note);

          // Restoring selection is heuristic because content changed length.
          // Ideally we would transform positions but for now we try to keep similar index.
          // We clamp to document size.
          const docSize = editor.state.doc.content.size;
          const newFrom = Math.min(from, docSize);
          const newTo = Math.min(to, docSize);

          try {
            editor.commands.setTextSelection({ from: newFrom, to: newTo });
          } catch (e) {
            // Fallback if selection invalid
          }
        }
      }
    }, [editor, note, noteId]);

    const handleSplitUp = useCallback(() => {
      if (editor) {
        editor.chain().focus().cutOffUpperPart().run();
        setMenuVisible(false);
      }
    }, [editor]);

    const handleSplitDown = useCallback(() => {
      if (editor) {
        editor.chain().focus().cutOffLowerPart().run();
        setMenuVisible(false);
      }
    }, [editor]);

    const contentMenu = useMemo(() => {
      if (!editor) {
        return null;
      }

      return (
        <ContextMenu
          items={[
            {
              leftIcon: 'H1',
              rightIcon: '#',
              label: 'Заголовок',
              onClick: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
            },
            {
              leftIcon: 'H2',
              rightIcon: '##',
              label: 'Заголовок',
              onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
            },
            {
              leftIcon: 'H3',
              rightIcon: '###',
              label: 'Заголовок',
              onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
            },
            { type: 'divider' },
            {
              leftIcon: <b>B</b>,
              rightIcon: '**',
              label: 'Жирный',
              onClick: () => editor.chain().focus().toggleBold().run(),
            },
            {
              leftIcon: <i>I</i>,
              rightIcon: '*',
              label: 'Курсив',
              onClick: () => editor.chain().focus().toggleItalic().run(),
            },
            { type: 'divider' },
            {
              leftIcon: 'Q',
              rightIcon: '>',
              label: 'Цитата',
              onClick: () => editor.chain().focus().toggleBlockquote().run(),
            },
            {
              leftIcon: <List16 />,
              rightIcon: '-',
              label: 'Список',
              onClick: () => editor.chain().focus().toggleBulletList().run(),
            },
            { type: 'divider' },
            {
              leftIcon: 'TK',
              rightIcon: '[TK]',
              label: 'Метка TK',
              onClick: () => editor.chain().focus().insertTkMarker().run(),
            },
            { type: 'divider' },
            {
              leftIcon: <AlignCenterV16 />,
              label: 'Разделить заметку',
              children: [
                { label: 'Отделить верхнюю часть', onClick: handleSplitUp },
                { label: 'Отделить нижнюю часть', onClick: handleSplitDown },
              ],
            },
          ]}
        />
      );
    }, [editor, handleSplitDown, handleSplitUp]);

    if (!editor) return null;

    return (
      <>
        <EditorContent editor={editor} />

        {mobile && <BubbleMenu editor={editor}>{contentMenu}</BubbleMenu>}

        {menuVisible && (
          <MenuBox x={menuPosition.layerX} y={menuPosition.layerY} ref={menuRef}>
            {contentMenu}
          </MenuBox>
        )}
      </>
    );
  },
);

TipTapEditor.displayName = 'TipTapEditor';
